<script setup>
import { ref } from 'vue';

import { useTheme } from '../composables/useTheme';
import HrOnOffSwitch from './HrOnOffSwitch.vue';

/**
 * Setting switch — a full-width row combining a label, optional description, and an on/off toggle.
 *
 * A SWITCH STATES WHAT IS TRUE RIGHT NOW, which is why Material 3 reserves it for changes that
 * apply the moment they are flipped. Inside a form that commits on Save, a switch left in the on
 * position reads as a setting already in force when nothing has been written yet — so the control
 * itself makes a claim the screen cannot keep. Use a checkbox there: it says "this is what I want",
 * not "this is how things are".
 *
 * @status recommended
 * @group forms-input
 * @useWhen A named on/off setting that takes effect the moment it is switched
 * @avoidWhen The change is committed by a Save or confirm button — use HrCheckbox
 * @related HrOnOffSwitch
 * @related HrCheckbox
 * @a11y The label area has role="button" and responds to Enter and Space. The toggle has native switch role
 *   and takes the label as its accessible name, which a screen reader would otherwise announce as "switch".
 * @event update:modelValue Forwarded from HrOnOffSwitch; new boolean value.
 */
defineOptions({ name: 'HrSettingSwitch' });

const props = defineProps({
  /**
   * Setting label displayed on the left side.
   * @example Allow notifications
   */
  label: {
    type: String,
    required: true,
  },
  /**
   * Supporting text below the label: one sentence the reader needs to decide.
   * A longer or occasional explanation goes behind an HrIconHintBtn, never in a tooltip.
   */
  description: {
    type: String,
    default: null,
  },
  /** Current on/off state. */
  modelValue: {
    type: Boolean,
    default: false,
  },
  /** Prevents interaction and dims the entire row. */
  disable: {
    type: Boolean,
    default: false,
  },
});

const { hasTheme } = useTheme();
const switchRef = ref(null);

function toggle() {
  if (!props.disable) {
    switchRef.value.$el.click();
  }
}
</script>
<template>
  <div
    class="hr-setting-switch"
    :class="{ 'hr-setting-switch--themed': hasTheme, 'hr-setting-switch--disabled': disable }"
  >
    <div
      class="hr-setting-switch__text"
      :class="{ 'hr-setting-switch__text--clickable': !disable }"
      :tabindex="disable ? undefined : 0"
      role="button"
      @click="toggle"
      @keydown.enter.prevent="toggle"
      @keydown.space.prevent="toggle"
    >
      <span
        class="hr-setting-switch__label"
        :class="hasTheme ? 'hr-body-large' : undefined"
      >{{ label }}</span>
      <span
        v-if="description"
        class="hr-setting-switch__description"
        :class="hasTheme ? 'hr-body-medium' : undefined"
      >{{ description }}</span>
    </div>
    <hr-on-off-switch
      ref="switchRef"
      :model-value="modelValue"
      :disable="disable"
      :aria-label="label"
      v-bind="$attrs"
    />
  </div>
</template>
<style lang="scss" scoped>
.hr-setting-switch {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  width: 100%;
}

.hr-setting-switch__text {
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 0;
}

.hr-setting-switch__text--clickable {
  cursor: pointer;
}

/* Only applies to light/dark themes */
.hr-setting-switch--themed .hr-setting-switch__label {
  color: var(--app-on-surface);
}

.hr-setting-switch--themed .hr-setting-switch__description {
  color: var(--app-on-surface-variant);
}

.hr-setting-switch--themed.hr-setting-switch--disabled .hr-setting-switch__label,
.hr-setting-switch--themed.hr-setting-switch--disabled .hr-setting-switch__description {
  color: color-mix(in srgb, var(--app-on-surface) 38%, transparent);
}
</style>
